import { Dispatch } from '@reduxjs/toolkit';

import { setTitleCreditsData, TitleCreditsData } from './monitor.slice';

export const titleCreditsJobKey = (data: Pick<TitleCreditsData, 'storyId' | 'key'>) => `${data.storyId}:${data.key}`;

type QueuedJob = { payload: TitleCreditsData; resolve: (value: unknown) => void };

const jobQueue: QueuedJob[] = [];
let isRendering = false;

const startNextTitleCreditsJob = (dispatch: Dispatch) => {
  if (isRendering || jobQueue.length === 0) return;
  isRendering = true;
  dispatch(setTitleCreditsData(jobQueue[0].payload));
};

/** Enqueue overlay render/upload; resolves when TitleCreditsGenerator completes this job. */
export const enqueueTitleCreditsJob = (
  payload: TitleCreditsData,
  resolve: (value: unknown) => void,
  dispatch: Dispatch
) => {
  const key = titleCreditsJobKey(payload);
  for (let i = jobQueue.length - 1; i >= 1; i--) {
    if (titleCreditsJobKey(jobQueue[i].payload) === key) {
      jobQueue[i].resolve('');
      jobQueue.splice(i, 1);
    }
  }

  jobQueue.push({ payload, resolve });
  startNextTitleCreditsJob(dispatch);
};

/** Called after domToImage capture + upload for the active (head) job. */
export const completeTitleCreditsJob = (payload: TitleCreditsData, dispatch: Dispatch) => {
  const head = jobQueue[0];
  if (!head || !isRendering) return;
  if (head.payload.storyId !== payload.storyId || head.payload.key !== payload.key) {
    // Self-heal: still advance the queue on a mismatch, or every future title/credits
    // job (any story) would silently stop generating for the rest of the session.
    console.error('[title-credits-queue] completeTitleCreditsJob: payload does not match active job, resetting queue');
  }

  head.resolve('');
  jobQueue.shift();
  isRendering = false;
  dispatch(setTitleCreditsData(null));
  startNextTitleCreditsJob(dispatch);
};

/** Test helper */
export const resetTitleCreditsQueue = () => {
  jobQueue.forEach((job) => job.resolve(''));
  jobQueue.length = 0;
  isRendering = false;
};

/** Test helper */
export const getTitleCreditsQueueState = () => ({
  queueLength: jobQueue.length,
  activeKey: isRendering && jobQueue[0] ? titleCreditsJobKey(jobQueue[0].payload) : null,
  pendingCount: jobQueue.length,
});
